import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, IconButton, Stack, Tooltip, Typography} from 'views/components';
import {AddOutlined, RefreshOutlined} from 'views/icons';
import {CaregiverAutocomplete, ClientAutocomplete, Translate, translate} from 'views/module';

import ChannelInboxItem from './ChannelInboxItem';
import NewChannelDialog from './NewChannelDialog';

const ChannelInbox = (props) => {
  const {
    channels,
    channelTypeKey,
    channelTypes,
    setChannelTypeKey,
    isLoading,
    loadFailed,
    getChannelTitle,
    isUnread,
    lastEvaluatedKey,
    loadMoreChannels,
    refreshChannels,
    createChannel,
    selectedCaregiver,
    setSelectedCaregiver,
    selectedClient,
    setSelectedClient,
    selectedConversationRn,
    selectConversationRn,
    unreadCount,
    user,
  } = props;

  const [isNewChannelDialogOpen, setIsNewChannelDialogOpen] = React.useState(false);

  const caregiverStatuses = Object.keys(translate('caregiver.Status'));
  const clientStatuses = Object.keys(translate('client.Status'));
  const canCreateChannel = channelTypeKey === 'SUPPORT';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        width: {xs: '100%', md: '320px'},
        borderRight: {md: '1px solid #ECECF1'},
        minHeight: {xs: '360px', md: 0},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          p: '22px 18px 14px 20px',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
          <Typography sx={{fontSize: '16px', fontWeight: 700, color: '#050A14'}}>
            <Translate expression='inbox.List.Title' />
          </Typography>
          <Stack direction='row' sx={{gap: 0.75, alignItems: 'center'}}>
            <Typography sx={{fontSize: '12px', fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap'}}>
              {translate('inbox.List.UnreadCount', {count: unreadCount})}
            </Typography>
            <Typography sx={{fontSize: '12px', fontWeight: 500, color: '#9A9BAE', whiteSpace: 'nowrap'}}>
              {translate('inbox.List.ChannelCount', {count: channels.length})}
            </Typography>
            {
              canCreateChannel && (
                <Tooltip title={translate('inbox.NewChannel.Title')}>
                  <IconButton
                    aria-label={translate('inbox.NewChannel.Title')}
                    onClick={() => setIsNewChannelDialogOpen(true)}
                    size='small'
                    sx={{
                      'flexShrink': 0,
                      'color': '#63708A',
                      '&:hover': {backgroundColor: '#F7F8FA'},
                    }}
                  >
                    <AddOutlined sx={{fontSize: 16}} />
                  </IconButton>
                </Tooltip>
              )
            }
            <Tooltip title={translate('inbox.List.Refresh')}>
              <IconButton
                aria-label={translate('inbox.List.Refresh')}
                disabled={isLoading}
                onClick={refreshChannels}
                size='small'
                sx={{
                  'flexShrink': 0,
                  'color': '#63708A',
                  '&:hover': {backgroundColor: '#F7F8FA'},
                }}
              >
                <RefreshOutlined sx={{fontSize: 16}} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        <CaregiverAutocomplete
          label={translate('inbox.List.Caregiver.Label')}
          selectedCaregiver={selectedCaregiver}
          setSelectedCaregiver={setSelectedCaregiver}
          statuses={caregiverStatuses}
          onClear={() => setSelectedCaregiver(null)}
        />
        {
          channelTypeKey === 'CLIENT' && (
            <ClientAutocomplete
              label={translate('inbox.List.Client.Label')}
              selectedClient={selectedClient}
              setSelectedClient={setSelectedClient}
              statuses={clientStatuses}
              onClear={() => setSelectedClient(null)}
            />
          )
        }
        <Stack direction='row' sx={{gap: 0.75, flexWrap: 'wrap'}}>
          {
            channelTypes.map((channelType) => (
              <Button
                aria-pressed={channelTypeKey === channelType}
                disabled={isLoading}
                key={channelType}
                onClick={() => setChannelTypeKey(channelType)}
                sx={{
                  'height': '30px',
                  'px': '12px',
                  'py': '6px',
                  'border': '1px solid',
                  'borderColor': channelTypeKey === channelType ? '#EB7A58' : '#DCDDE5',
                  'borderRadius': '999px',
                  'backgroundColor': channelTypeKey === channelType ? '#FFF4EE' : '#FFFFFF',
                  'boxShadow': 'none',
                  'color': channelTypeKey === channelType ? '#C4512C' : '#526071',
                  'fontSize': '12px',
                  'fontWeight': 700,
                  'lineHeight': '16px',
                  'textTransform': 'none',
                  'minWidth': 0,
                  '&:hover': {
                    'borderColor': channelTypeKey === channelType ? '#D96845' : '#D1D3DD',
                    'backgroundColor': channelTypeKey === channelType ? '#FFF1EA' : '#F7F8FA',
                    'boxShadow': 'none',
                  },
                }}
                variant='outlined'
              >
                {translate(`inbox.List.ChannelType.Options.${channelType}`)}
              </Button>
            ))
          }
        </Stack>
      </Box>
      <Stack
        sx={{
          'gap': 1,
          'flex': 1,
          'minHeight': 0,
          'overflowY': 'auto',
          'px': 2,
          'pb': 1.5,
          'pt': 0.75,
          'scrollbarColor': '#CFCFD4 transparent',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#CFCFD4',
            borderRadius: '999px',
          },
        }}
      >
        {
          channels.map((channel) => (
            <ChannelInboxItem
              key={channel.conversationRn}
              channel={channel}
              isSelected={channel.conversationRn === selectedConversationRn}
              channelTitle={getChannelTitle(channel)}
              isUnread={isUnread(channel)}
              onSelect={selectConversationRn}
              user={user}
            />
          ))
        }
        {
          isLoading && channels.length === 0 && (
            <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#6D7280', px: 1, py: 2}}>
              <Translate expression='inbox.List.Loading' />
            </Typography>
          )
        }
        {
          loadFailed && (
            <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#D85D31', px: 1, py: 2}}>
              <Translate expression='inbox.List.LoadFailed' />
            </Typography>
          )
        }
        {
          !isLoading && !loadFailed && channels.length === 0 && (
            <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#6D7280', px: 1, py: 2}}>
              <Translate expression='inbox.List.Empty' />
            </Typography>
          )
        }
      </Stack>
      {
        lastEvaluatedKey && (
          <Box sx={{p: 1.5, borderTop: '1px solid #ECECF1'}}>
            <Button
              disabled={isLoading}
              fullWidth
              onClick={loadMoreChannels}
              sx={{
                'height': '32px',
                'px': '10px',
                'py': '8px',
                'border': '1px solid #DCDDE5',
                'borderRadius': '7px',
                'backgroundColor': '#FFFFFF',
                'color': '#6D7280',
                'textTransform': 'none',
                'boxShadow': 'none',
                '&:hover': {
                  'border': '1px solid #D1D3DD',
                  'backgroundColor': '#F7F8FA',
                  'boxShadow': 'none',
                },
              }}
              variant='outlined'
            >
              {translate('inbox.List.LoadMore')}
            </Button>
          </Box>
        )
      }
      {
        canCreateChannel && isNewChannelDialogOpen && (
          <NewChannelDialog
            closeDialog={() => setIsNewChannelDialogOpen(false)}
            createChannel={createChannel}
          />
        )
      }
    </Box>
  );
};

ChannelInbox.propTypes = {
  channels: PropTypes.array.isRequired,
  channelTypeKey: PropTypes.string.isRequired,
  channelTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  setChannelTypeKey: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadFailed: PropTypes.bool.isRequired,
  getChannelTitle: PropTypes.func.isRequired,
  isUnread: PropTypes.func.isRequired,
  lastEvaluatedKey: PropTypes.string,
  loadMoreChannels: PropTypes.func.isRequired,
  refreshChannels: PropTypes.func.isRequired,
  createChannel: PropTypes.func.isRequired,
  selectedCaregiver: PropTypes.shape({
    caregiverRn: PropTypes.string,
    caregiverName: PropTypes.string,
  }),
  setSelectedCaregiver: PropTypes.func.isRequired,
  selectedClient: PropTypes.shape({
    clientRn: PropTypes.string,
    clientName: PropTypes.string,
  }),
  setSelectedClient: PropTypes.func.isRequired,
  selectedConversationRn: PropTypes.string,
  selectConversationRn: PropTypes.func.isRequired,
  unreadCount: PropTypes.number.isRequired,
  user: PropTypes.object,
};

export default React.memo(ChannelInbox);
